/** §5 ICF rule checks — badges only, never blocking. */
import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { IcfRuleCheck, IcfRuleKey } from '@/types/mentorEngagements';
import { Chip, Panel, mentorStyles } from './shared';

/** Resolve locally by rule key so the labels survive whatever the backend emits. */
const RULE_LABEL_KEYS: Record<IcfRuleKey, string> = {
  total_hours: 'mentorEngagements.rules.totalHours',
  individual_hours: 'mentorEngagements.rules.individualHours',
  observed_sessions: 'mentorEngagements.rules.observedSessions',
  engagement_span: 'mentorEngagements.rules.engagementSpan',
  session_metadata: 'mentorEngagements.rules.sessionMetadata',
  c1_verified: 'mentorEngagements.rules.c1Verified',
};

export function ruleLabelKey(check: IcfRuleCheck): string {
  return RULE_LABEL_KEYS[check.key] || check.message_key;
}

export function IcfRulesPanel({ checks, compact = false }: { checks: IcfRuleCheck[]; compact?: boolean }) {
  const [expanded, setExpanded] = useState(!compact);
  const { t } = useTranslation();

  const body = (
    <>
      <Pressable testID="workspace_rule_checks" accessibilityRole="button" accessibilityState={{ expanded }} onPress={() => setExpanded(!expanded)}>
        <Text style={mentorStyles.bodyText}>{t('mentorEngagements.rules.title')} · {checks.filter(c => c.ok).length}/{checks.length} {expanded ? '−' : '+'}</Text>
      </Pressable>
      {!compact ? <Text style={mentorStyles.panelSubtitle}>{t('mentorEngagements.rules.subtitle')}</Text> : null}
      {expanded ? <View style={{ gap: 8 }}>
        {checks.map((check) => (
          <View
            key={check.key}
            testID={`engagement_rule_${check.key}`}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              paddingVertical: 9,
              borderTopWidth: 1,
              borderTopColor: reviewDesk.line,
            }}
          >
            <MaterialIcons
              name={check.ok ? 'check-circle' : 'error-outline'}
              size={18}
              color={check.ok ? reviewDesk.success : reviewDesk.amber}
            />
            <Text style={[mentorStyles.bodyText, mentorStyles.flexOne]}>
              {t(ruleLabelKey(check))}
            </Text>
            {check.detail ? <Text style={[mentorStyles.mutedText, { flexShrink: 1, maxWidth: compact ? 110 : 200 }]}>{check.detail}</Text> : null}
            <Chip
              label={check.ok ? t('mentorEngagements.rules.pass') : t('mentorEngagements.rules.fail')}
              tone={check.ok ? 'success' : 'warning'}
            />
          </View>
        ))}
      </View> : null}
    </>
  );
  return compact ? <View testID="engagement_rules_panel" style={{ gap: 8 }}>{body}</View> : <Panel testID="engagement_rules_panel">{body}</Panel>;
}

export default IcfRulesPanel;
